import {
  isRouteErrorResponse,
  useLocation,
  useNavigate,
  useRouteError,
} from "react-router";
import * as Sentry from "@sentry/react-router";
import { useCallback, useEffect, type ReactNode } from "react";
import { Frown } from "lucide-react";
import { ReactivButton } from "./atomics/Button";

export function navigateViaShopifyAdmin(appPath: string) {
  try {
    const storeUrl = sessionStorage.getItem("shopify_store_url");
    const apiKey = sessionStorage.getItem("shopify_api_key");

    if (storeUrl && apiKey) {
      const storeName = storeUrl
        .replace(".myshopify.com", "")
        .replace("https://", "");
      const cleanPath = appPath.startsWith("/") ? appPath.slice(1) : appPath;
      window.top!.location.href = `https://admin.shopify.com/store/${storeName}/apps/${apiKey}/${cleanPath}`;
      return;
    }
  } catch {
    // sessionStorage or window.top may be unavailable
  }
}

interface ErrorLayoutProps {
  message: string;
  subMessage?: ReactNode;
  actions?: ReactNode;
}

function ErrorLayout({ message, subMessage, actions }: ErrorLayoutProps) {
  return (
    <div className="flex flex-col items-center justify-center h-screen bg-white text-center px-4">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-800 mb-2">Oh!</h1>
        <div className="flex flex-col items-center justify-center mb-4">
          <Frown className="!w-16 !h-16" />
        </div>
        <p className="text-gray-600 mb-6">{message}</p>
        {subMessage && <p className="text-gray-600 mb-6">{subMessage}</p>}
      </div>
      {actions && <div className="flex justify-center space-x-4">{actions}</div>}
    </div>
  );
}

type ProviderErrorType = "section" | "menu" | "block" | "screen" | null;

function getProviderErrorType(error: unknown): ProviderErrorType {
  if (!(error instanceof Error)) return null;

  const message = error.message;
  if (message.startsWith("SectionProvider: Section not found")) return "section";
  if (message.startsWith("MenuProvider: Menu not found")) return "menu";
  if (message.startsWith("BlockProvider: Block not found")) return "block";
  if (message.startsWith("ScreenProvider: Screen not found")) return "screen";

  return null;
}

function getRedirectPath(pathname: string): { path: string; screenName: string; wouldLoop: boolean } {
  const isClips = pathname.includes("/clips/");
  const parts = pathname.split("/");

  // Path structure: /app/studio/{screen}/... (index 3) vs /app/clips/studio/{screen}/... (index 4)
  const screenIndex = isClips ? 4 : 3;
  const screenName = parts[screenIndex] || "home";

  const basePath = isClips ? "/app/clips/studio" : "/app/studio";
  const parentPath = `${basePath}/${screenName}`;

  const wouldLoop = pathname === parentPath || pathname === `${parentPath}/`;

  return { path: parentPath, screenName, wouldLoop };
}

export const ErrorBoundaryReactiv = () => {
  const error = useRouteError();
  const location = useLocation();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const navigate = useNavigate();
  const isClips = location.pathname.includes("/clips/");
  const homePath = isClips ? "/app/clips/studio/home" : "/app/studio/home";

  const navigateToHome = useCallback(() => {
    navigate(homePath, { replace: true });
  }, [homePath, navigate]);

  const { path: parentPath, screenName, wouldLoop } = getRedirectPath(location.pathname);
  const providerErrorType = getProviderErrorType(error);

  const navigateToParent = useCallback(() => {
    navigate(parentPath, { replace: true });
  }, [parentPath, navigate]);

  const canAutoRedirect = providerErrorType && !wouldLoop;

  useEffect(() => {
    if (canAutoRedirect) {
      navigateToParent();
    }
  }, [canAutoRedirect, navigateToParent]);

  if (providerErrorType && wouldLoop) {
    return (
      <ErrorLayout
        message={`This ${providerErrorType} is no longer available.`}
        actions={
          <ReactivButton variant="primary" onClick={navigateToHome}>
            Go to homepage
          </ReactivButton>
        }
      />
    );
  }

  if (canAutoRedirect) {
    return (
      <ErrorLayout
        message={`The ${providerErrorType} you were editing is no longer available.`}
        subMessage={<>Returning to {screenName} now...</>}
      />
    );
  }

  if (isRouteErrorResponse(error) || location.pathname.includes("/app/")) {
    return (
      <ErrorLayout
        message="Something went wrong!!"
        subMessage={
          <>
            We're having trouble processing your request right now.
            <br />
            While we investigate what went wrong, you can try reloading the
            page,
            <br />
            return to the homepage, or chat with us.
          </>
        }
        actions={
          <ReactivButton variant="primary" onClick={navigateToHome}>
            Go to homepage
          </ReactivButton>
        }
      />
    );
  }

  return (
    <ErrorLayout
      message="Something went wrong!!"
      subMessage={
        <>
          We're having trouble processing your request right now.
          <br />
          Contact Reactiv Support to investigate what went wrong.
        </>
      }
    />
  );
};
